import { type Container, createIdentifier } from '@blocksuite/global/di';
import { BlockSuiteError } from '@blocksuite/global/exceptions';
import { type BlockStdScope, StdIdentifier } from '@blocksuite/std';
import { type BlockSnapshot, Extension, type Store } from '@blocksuite/store';

import { getSurfaceComponent } from '../utils/get-surface-block';
import { EdgelessCRUDIdentifier } from './crud-identifier';

export type ClipboardConfigCreationContext = {
  /**
   * element old id to new id
   */
  oldToNewIdMap: Map<string, string>;
  /**
   * element old id to new layer index
   */
  originalIndexes: Map<string, string>;

  /**
   * frame old id to new presentation index
   */
  newPresentationIndexes: Map<string, string>;
};

/** One pasted block whose asynchronous work is done. */
export type PreparedEdgelessBlock = {
  /** Writes the block. It runs in the one transaction of the whole paste and
   * must not await; the new ID, null when nothing was written. */
  create(context: ClipboardConfigCreationContext): string | null;
  /** Runs after that transaction: removes a failed write again and inserts
   * what the owner of a block inserts itself (a cut database); the ID of the
   * block once it exists. */
  settle?(): Promise<string | null>;
};

export const EdgelessClipboardConfigIdentifier =
  createIdentifier<EdgelessClipboardConfig>('edgeless-clipboard-config');

export abstract class EdgelessClipboardConfig extends Extension {
  static key: string;

  constructor(readonly std: BlockStdScope) {
    super();
  }

  get surface() {
    return getSurfaceComponent(this.std);
  }

  get crud() {
    return this.std.get(EdgelessCRUDIdentifier);
  }

  /** Prepares `snapshot` as a child of `parent` through the transformer. */
  async prepareSnapshotPaste(
    snapshot: BlockSnapshot,
    doc: Store,
    parent?: string
  ): Promise<PreparedEdgelessBlock | null> {
    const prepared = await this.std.clipboard.prepareBlockSnapshot(
      snapshot,
      doc,
      parent
    );
    if (!prepared) return null;
    return {
      create: () => (prepared.write() ? snapshot.id : null),
      settle: async () => (await prepared.settle())?.id ?? null,
    };
  }

  /**
   * The asynchronous work of pasting `snapshot`: asset checks and the
   * conversion of its snapshot. Writes nothing; null skips the block.
   */
  abstract prepareBlock(
    snapshot: BlockSnapshot
  ): PreparedEdgelessBlock | null | Promise<PreparedEdgelessBlock | null>;

  static override setup(di: Container) {
    if (!this.key) {
      throw new BlockSuiteError(
        BlockSuiteError.ErrorCode.ValueNotExists,
        'Key is not defined in the EdgelessClipboardConfig'
      );
    }

    di.add(
      this as unknown as { new (std: BlockStdScope): EdgelessClipboardConfig },
      [StdIdentifier]
    );

    di.addImpl(EdgelessClipboardConfigIdentifier(this.key), provider =>
      provider.get(this)
    );
  }
}
